import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, OnDestroy } from '@angular/core';
import { ColDef, GridApi, GridOptions, GridReadyEvent, ProcessUnpinnedColumnsParams, RowSelectedEvent, RowSelectionOptions } from 'ag-grid-community';
import { Router } from '@angular/router';
import { ForecastService } from '../../../modules/forecast/services/forecast.service';
import { GainsGridComponent } from '@gainsystems/gains-ui-commons';
import { GainsGridPagination } from 'app/shared/models/gains-grid-pagination';
import { SelectOption } from '@gainsystems/gains-ui-commons';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { GainsFilterBarComponent } from '@gainsystems/gains-ui-commons';
import { UiStateStorageService } from 'app/shared/services/ui-state-storage/ui-state-storage.service';

/**
 * GainsSkuLocationsPanelComponent
 *
 * @param defaultAdditionalColWidth - The default width to apply to additional columns that do not specify a width. Default: 120.
 * @param autoApplyDefaultColWidth - If true, columns without a width will be set to defaultAdditionalColWidth. Default: true. Set to false to disable this behavior.
 */
@Component({
  selector: 'gains-sku-locations-panel',
  templateUrl: './gains-sku-locations-panel.component.html',
  standalone: false,
  styleUrls: ['./gains-sku-locations-panel.component.scss']
})
export class GainsSkuLocationsPanelComponent implements OnInit, OnChanges, OnDestroy {
  /**
   * The default width to apply to additional columns that do not specify a width.
   * Can be set by the parent component. Default: 120.
   */
  @Input() defaultAdditionalColWidth = 120;
  /**
   * If true, columns without a width will be set to defaultAdditionalColWidth.
   * Set to false to disable this behavior. Default: true.
   */
  @Input() autoApplyDefaultColWidth = true;
  /**
   * Unique prefix for localStorage state key to separate state between different screens.
   * Allows forecast and prediction screens to maintain independent column configurations.
   * Default: 'default'
   */
  @Input() stateKeyPrefix = 'default';

  @Input() skuLocationIndex?: number; // 0-based index for the selected SKU location (optional - allows state restoration)
  @Input() additionalColumnDefs: ColDef[] = [];
  @Input() defaultColumnDefs?: ColDef[] = []; // Optional input for default column definition
  @ViewChild(GainsFilterBarComponent) filterBar?: GainsFilterBarComponent;
  @Output() skuLocationSelected = new EventEmitter<GainsGridPagination>();
  @Output() filterChanged = new EventEmitter<void>();
  @Input() skuLocations: any[] | null = null;
  @Input() selectedSkuLocations: any[] = [];
  @ViewChild(GainsGridComponent, { static: true }) gainsGrid?: GainsGridComponent;
  gridOptions: GridOptions = {
    suppressColumnVirtualisation: true,
    suppressHorizontalScroll: false,
    suppressMovableColumns: true, // Prevent columns from being moved by dragging
    defaultColDef: {
      suppressMovable: true // Prevents all columns from being dragged
    }
  };
  quickFilterText = '';
  searchInputValue = '';
  gridApi!: GridApi;
  private readonly searchSubject = new Subject<string>();
  private isRestoringState = false;
  private isStateInitializationPending = true;

  selectionColumnDef = {
    sortable: false,
    resizable: true,
    width: 50,
    suppressHeaderMenuButton: true
  };

  columnDefs: ColDef[] = [
    /* This code is part of phase 2. I'll keep it commented because this implementation does not use out-of-the-box checkbox selection from ag-grid.
     {
      headerName: '',
      field: 'checked',
      pinned: 'left',
      width: 50,
      cellRenderer: (params: any) => {
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.checked = !!params.data.checked;

        input.addEventListener('click', event => {
          event.stopPropagation(); // Prevent row selection
          params.data.checked = input.checked;
          this.selectedRowsForPopup.set(params.data.id, params.data.checked);
        });
        return input;
      }
    }, */
    {
      field: 'item',
      headerName: 'Item',
      width: 150,
      cellRenderer: (params: any) => {
        return `${params.value} <span class="chevron">›</span>`;
      }
    },
    { field: 'location', headerName: 'Location', width: 120 }
  ];

  defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    suppressMovable: true // Prevents all columns from being dragged
  };
  rowSelection = {
    mode: 'singleRow',
    checkboxes: false,
    enableClickSelection: true
  } as RowSelectionOptions;

  constructor(
    private readonly _forecastService: ForecastService,
    private readonly router: Router,
    private readonly _uiStateStorage: UiStateStorageService
  ) {}

  /**
   * Get the localStorage key with the current context prefix
   */
  private getStorageKey(): string {
    return `${this.getEffectiveStateKeyPrefix()}_skuLocationsGridState`;
  }

  /**
   * Resolve state key prefix for this panel instance.
   * Uses explicit input when provided, otherwise derives from the current screen route.
   */
  private getEffectiveStateKeyPrefix(): string {
    if (this.stateKeyPrefix && this.stateKeyPrefix !== 'default') {
      return this.stateKeyPrefix;
    }

    const topLevelRoute = this.router.url.split('?')[0].split('#')[0].split('/').filter(Boolean)[0];

    return topLevelRoute || 'default';
  }
  getCurrentColDefs = (): ColDef[] => {
    return this.gridApi.getColumnDefs()!;
  };
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['skuLocationIndex'] && this.gridApi) {
      const currentValue = changes['skuLocationIndex'].currentValue;

      // Only apply selection if a valid index is provided (not undefined or null)
      if (currentValue !== undefined && currentValue !== null) {
        const zeroBasedIndex = currentValue - 1;

        this.gridApi.deselectAll();

        // Select the row at the specified index in the filtered/sorted data
        this.gridApi.forEachNodeAfterFilterAndSort((node, index) => {
          if (index === zeroBasedIndex) {
            node.setSelected(true);
            if (node.rowIndex !== null) {
              this.gridApi.ensureIndexVisible(node.rowIndex);
            }
          }
        });
      }
    }
  }

  ngOnInit(): void {
    // Set up debounced search
    this.searchSubject
      .pipe(
        debounceTime(300), // Wait 300ms after user stops typing
        distinctUntilChanged() // Only emit if the value has changed
      )
      .subscribe(searchText => {
        this.performSearch(searchText);
      });

    // Use input defaultColumnDefs if provided
    if (this.defaultColumnDefs) {
      this.columnDefs.push(...this.defaultColumnDefs);
    }
    // Optionally set fixed width for all additional columns
    this.additionalColumnDefs.forEach(col => {
      if (this.autoApplyDefaultColWidth && !col.width) {
        col.width = this.defaultAdditionalColWidth;
      }
    });
    this.columnDefs.push(...this.additionalColumnDefs);
    this.loadSkuLocations();
  }

  onColumnresize(event: any) {
    event.columns.forEach((col: any) => {
      if (this.autoApplyDefaultColWidth && !col.width) {
        col.minWidth = this.defaultAdditionalColWidth;
      }
    });
  }

  onSearchRuleChange(searchText: string): void {
    // Emit the search text to the subject for debouncing
    this.searchSubject.next(searchText);
  }

  private performSearch(searchText: string): void {
    if (this.gainsGrid) {
      this.quickFilterText = searchText?.toLowerCase() || '';
      this.gainsGrid.invokeOnFilterChanged();

      // After filter is applied, check if we need to update selection
      setTimeout(() => {
        this.updateSelectionAfterFilter();
      }, 0);
    }
  }

  private updateSelectionAfterFilter(): void {
    if (!this.gridApi) return;

    const selectedRows = this.gridApi.getSelectedRows();
    let filteredTotalItems = 0;

    // Count total filtered items
    this.gridApi.forEachNodeAfterFilterAndSort((node, index) => {
      filteredTotalItems++;
    });

    // Handle case where no items match the filter
    if (filteredTotalItems === 0) {
      this.gridApi.deselectAll();

      this.skuLocationSelected.emit({
        skulId: 0,
        currentItemIndex: -1,
        totalItems: 0,
        item: '',
        location: ''
      } as GainsGridPagination);

      return;
    }

    // If we have filtered items
    if (selectedRows.length > 0) {
      const selectedSkuId = selectedRows[0].skulId;
      let newIndex = -1;

      // Find if selected item is still visible
      this.gridApi.forEachNodeAfterFilterAndSort((node, index) => {
        if (node.data.skulId === selectedSkuId) {
          newIndex = index;
        }
      });

      if (newIndex === -1) {
        this.defineASelectedRow(0);
      } else {
        this.skuLocationSelected.emit({
          skulId: selectedSkuId,
          item: selectedRows[0].item,
          location: selectedRows[0].location,
          currentItemIndex: newIndex,
          totalItems: filteredTotalItems
        } as GainsGridPagination);
      }
    } else {
      this.defineASelectedRow(0);
    }
  }
  loadSkuLocations(): void {
    if ((this.skuLocations?.length ?? 0) > 0) {
      return;
    }

    // Check if current URL contains 'demand-prediction'
    const isDemandPrediction = this.router.url.includes('/demand-prediction');

    this.skuLocations = null;

    this._forecastService.getSkuLocations(isDemandPrediction).subscribe(data => {
      this.skuLocations = data;
      if (this.gridApi) {
        // Data is bound via [rowData], wait for Angular to update the grid
        setTimeout(() => {
          const stateRestored = this.restoreGridState();
          if (!stateRestored) {
            this.defineASelectedRow();
          }
          this.isStateInitializationPending = false;
        }, 0);
      }
    });
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.columnDefs = this.columnDefs.map(col => this.applyColumnSizingDefaults(col));

    // If data already loaded before grid was ready, select row now
    if ((this.skuLocations?.length ?? 0) > 0) {
      setTimeout(() => {
        const stateRestored = this.restoreGridState();
        if (!stateRestored) {
          this.defineASelectedRow();
        }
        this.isStateInitializationPending = false;
      }, 0);
    }

    // Listen for sort and filter changes to update navigation
    this.gridApi.addEventListener('sortChanged', this.onSortOrFilterChanged.bind(this));
    this.gridApi.addEventListener('filterChanged', this.onSortOrFilterChanged.bind(this));
    this.gridApi.addEventListener('columnResized', this.onColumnresize.bind(this));

    // Listen for column state changes to save state
    this.gridApi.addEventListener('columnVisible', this.onColumnVisibilityChanged.bind(this));
    this.gridApi.addEventListener('columnMoved', this.onColumnMovedOrResized.bind(this));
    this.gridApi.addEventListener('newColumnsLoaded', this.onColumnStateChanged.bind(this));
    this.gridApi.addEventListener('gridColumnsChanged', this.onColumnStateChanged.bind(this));
    this.gridApi.addEventListener('displayedColumnsChanged', this.onColumnStateChanged.bind(this));
  }

  private onColumnStateChanged(): void {
    if (this.isRestoringState || !this.gridApi) return;
    this.syncColumnDefsWithGridState();
    this.saveGridState();
  }

  onColumnMovedOrResized(): void {
    // Skip syncing during state restoration to avoid change detection issues
    if (this.isRestoringState) return;

    // Sync column definitions with current grid state (order and visibility)
    if (this.gridApi) {
      const columnState = this.gridApi.getColumnState();

      // Create a new ordered array based on current grid state
      const newColumnDefs: ColDef[] = [];

      columnState.forEach(state => {
        const colDef = this.columnDefs.find(cd => cd.field === state.colId);
        if (colDef) {
          newColumnDefs.push({
            ...colDef,
            hide: state.hide || false,
            pinned: state.pinned,
            width: state.width ?? colDef.width
          });
        }
      });

      // Update columnDefs with the new order
      this.columnDefs.length = 0;
      this.columnDefs.push(...newColumnDefs.map(col => this.applyColumnSizingDefaults(col)));
    }
    this.saveGridState();
  }

  onColumnVisibilityChanged(): void {
    // Skip syncing during state restoration to avoid change detection issues
    if (this.isRestoringState) return;

    // Sync column definitions with current grid state
    if (this.gridApi) {
      const columnState = this.gridApi.getColumnState();

      // Update columnDefs to reflect current visibility state
      this.columnDefs.forEach(colDef => {
        const state = columnState.find(cs => cs.colId === colDef.field);
        if (state) {
          colDef.hide = state.hide || false;
          colDef.width = state.width ?? colDef.width;
        }
      });

      this.columnDefs = this.columnDefs.map(col => this.applyColumnSizingDefaults(col));
    }

    this.saveGridState();
  }

  defineASelectedRow(zeroBasedIndex = 0): void {
    this.gridApi.deselectAll();

    let selectedNode: any = null;
    let filteredTotalItems = 0;

    this.gridApi.forEachNodeAfterFilterAndSort((node, index) => {
      if (index === zeroBasedIndex) {
        node.setSelected(true);
        selectedNode = node;
        if (node.rowIndex !== null) {
          this.gridApi.ensureIndexVisible(node.rowIndex);
        }
      }
      filteredTotalItems++;
    });

    if (selectedNode) {
      this.skuLocationSelected.emit({
        skulId: selectedNode.data.skulId,
        item: selectedNode.data.item,
        location: selectedNode.data.location,
        currentItemIndex: zeroBasedIndex,
        totalItems: filteredTotalItems
      } as GainsGridPagination);
    }
  }
  onRowSelected($event: RowSelectedEvent<any>): void {
    this.selectedSkuLocations = this.gridApi.getSelectedRows();

    // Get the correct position in filtered/sorted data
    let filteredIndex = -1;
    let filteredTotalItems = 0;

    this.gridApi.forEachNodeAfterFilterAndSort((node, index) => {
      if (node.data.skulId === $event.data.skulId) {
        filteredIndex = index;
      }
      filteredTotalItems++;
    });

    this.skuLocationSelected.emit({
      skulId: $event.data.skulId,
      item: $event.data.item,
      location: $event.data.location,
      currentItemIndex: filteredIndex,
      totalItems: filteredTotalItems
    } as GainsGridPagination);

    // Save state when row is selected
    this.saveGridState();
  }

  // Called to tell AG Grid if an external filter is active
  isExternalFilterPresent = () => {
    return this.quickFilterText.trim() !== '';
  };

  // Custom filter logic
  doesExternalFilterPass = (node: any) => {
    const item = (node.data.item || '').toLowerCase();
    return item.includes(this.quickFilterText);
  };

  // Handle sort and filter changes to update the navigation
  onSortOrFilterChanged(): void {
    if (!this.gridApi) return;

    const selectedRows = this.gridApi.getSelectedRows();
    let filteredTotalItems = 0;

    // Count total filtered items
    this.gridApi.forEachNodeAfterFilterAndSort((node, index) => {
      filteredTotalItems++;
    });

    // Handle case where no items match the filter
    if (filteredTotalItems === 0) {
      this.gridApi.deselectAll();

      this.skuLocationSelected.emit({
        skulId: 0,
        currentItemIndex: -1,
        totalItems: 0,
        item: '',
        location: ''
      } as GainsGridPagination);

      return;
    }

    // If we have filtered items
    if (selectedRows.length > 0) {
      const selectedSkuId = selectedRows[0].skulId;
      let newIndex = -1;

      // Find the new position of the selected item in filtered/sorted data
      this.gridApi.forEachNodeAfterFilterAndSort((node, index) => {
        if (node.data.skulId === selectedSkuId) {
          newIndex = index;
        }
      });

      // Selected item is no longer visible after filtering
      if (newIndex === -1) {
        // Select the first item in the filtered data
        this.defineASelectedRow(0);
      } else {
        this.skuLocationSelected.emit({
          skulId: selectedSkuId,
          item: selectedRows[0].item,
          location: selectedRows[0].location,
          currentItemIndex: newIndex,
          totalItems: filteredTotalItems
        } as GainsGridPagination);
      }
    } else {
      this.defineASelectedRow(0);
    }
  }

  //  added this method to be called from parent component when the changes are made to the selected SKU location from its details panel
  public selectRowBySkulId(skulId: number) {
    if (!this.gridApi) return;

    let rowNode: any = null;
    let filteredIndex = -1;
    let filteredTotalItems = 0;

    this.gridApi.forEachNodeAfterFilterAndSort((node, index) => {
      if (node.data.skulId === skulId) {
        rowNode = node;
        filteredIndex = index;
      }
      filteredTotalItems++;
    });

    if (rowNode) {
      rowNode.setSelected(true);
      this.gridApi.ensureNodeVisible(rowNode, 'middle');

      this.skuLocationSelected.emit({
        skulId: rowNode.data.skulId,
        item: rowNode.data.item,
        location: rowNode.data.location,
        currentItemIndex: filteredIndex,
        totalItems: filteredTotalItems
      } as GainsGridPagination);
    }
  }

  saveGridState(): void {
    if (!this.gridApi || this.isRestoringState || this.isStateInitializationPending) return;

    const columnState = this.gridApi.getColumnState();
    if (!columnState || columnState.length === 0) return;
    const selectedRows = this.gridApi.getSelectedRows();
    const selectedSkulId = selectedRows && selectedRows.length > 0 ? selectedRows[0].skulId : null;

    // Preserve pinned state and enforce fixed-width behavior
    const cleanedColumnState = columnState.map(col => ({
      ...col,
      flex: undefined
    }));

    const gridState = {
      columnState: cleanedColumnState,
      filterModel: this.gridApi.getFilterModel(),
      selectedRowId: selectedSkulId
    };

    this._uiStateStorage.saveGridState(this.getStorageKey(), gridState);
    this._uiStateStorage.saveCustomState(this.getStorageKey(), {
      selectedSkulId,
      searchText: this.searchInputValue
    });
  }

  /**
   * Emit filter changed event to parent component
   */
  onFilterChanged(): void {
    // Save grid state including filters
    this.saveGridState();
    this.filterBar?.parseFilterModel();
    // Emit to parent so it can update filter chips
    this.filterChanged.emit();
  }

  filterBarChanged(): void {
    this.gainsGrid?.gridApi?.onFilterChanged();
  }

  /**
   * Sync columnDefs array with current grid state
   * Used after state restoration to ensure columnDefs matches grid display
   */
  private syncColumnDefsWithGridState(): void {
    if (!this.gridApi) return;

    const columnState = this.gridApi.getColumnState();
    const newColumnDefs: ColDef[] = [];

    columnState.forEach(state => {
      const colDef = this.columnDefs.find(cd => cd.field === state.colId);
      if (colDef) {
        newColumnDefs.push({
          ...colDef,
          hide: state.hide || false,
          pinned: state.pinned,
          width: state.width ?? colDef.width
        });
      }
    });

    if (newColumnDefs.length > 0) {
      this.columnDefs.length = 0;
      this.columnDefs.push(...newColumnDefs.map(col => this.applyColumnSizingDefaults(col)));
    }
  }

  private applyColumnSizingDefaults(colDef: ColDef): ColDef {
    const resolvedWidth = colDef.width ?? this.defaultAdditionalColWidth;
    return {
      ...colDef,
      width: resolvedWidth,
      minWidth: this.defaultAdditionalColWidth,
      resizable: true,
      suppressSizeToFit: true,
      flex: undefined
    };
  }

  private finalizeRestoreState(): void {
    this.syncColumnDefsWithGridState();
  }

  private restoreGridState(): boolean {
    if (!this.gridApi) return false;

    const pageKey = this.getStorageKey();
    const gridState = this._uiStateStorage.getGridState(pageKey);
    const customState = this._uiStateStorage.getCustomState(pageKey) || {};

    if (gridState) {
      this.isRestoringState = true;

      try {
        // Restore full column state (order, width, visibility, sort)
        if (gridState.columnState) {
          // Preserve pinned state and restore resized widths
          const cleanedState = gridState.columnState.map((state: any) => ({
            ...state,
            flex: undefined
          }));
          this.gridApi.applyColumnState({
            state: cleanedState,
            applyOrder: true // Preserve column order
          });
        }

        // Restore filters
        if (gridState.filterModel) {
          this.gridApi.setFilterModel(gridState.filterModel);
        }

        // Restore search text
        const savedSearchText = customState['searchText'];
        if (savedSearchText !== undefined) {
          this.searchInputValue = savedSearchText;
          this.quickFilterText = savedSearchText?.toLowerCase() || '';
          if (this.gainsGrid) {
            this.gainsGrid.invokeOnFilterChanged();
          }
        }

        // Restore selected row
        const selectedSkulId = (gridState.selectedRowId ?? customState['selectedSkulId']) as number | null;
        if (selectedSkulId) {
          // Use longer delay to ensure complete initialization before emitting events
          setTimeout(() => {
            this.selectRowBySkulId(selectedSkulId);
            this.isRestoringState = false;
            this.finalizeRestoreState();
          }, 150);
          return true;
        }

        this.finalizeRestoreState();
      } finally {
        setTimeout(() => {
          this.isRestoringState = false;
        }, 100);
      }
    }
    return false;
  }

  ngOnDestroy(): void {
    // Save state before destroying
    this.saveGridState();
    // Complete the subject to prevent memory leaks
    this.searchSubject.complete();
  }
}
